/* import __COLOCATED_TEMPLATE__ from './filterable-list.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import {
  ALL_CONTENT_COLUMN_DATA,
  CAN_MANAGE_KNOWLEDGE_BASE_CONTENT,
  DEFAULT_SELECTABLE_COLUMNS,
  FOLDER_MULTILINGUAL_DEFAULT_COLUMNS,
  KNOWLEDGE_HUB_EVERY_CONTENT_COLUMN_ORDER,
  KNOWLEDGE_HUB_REQUIRED_COLUMNS,
  IMPORT_SOURCE_STATUSES,
  States,
  type ColumnData,
  type SortDirection,
  type TagOperator,
} from 'embercom/lib/knowledge-hub/constants';
import {
  findRootSyncedFolder,
  getSyncSourceForFolder,
} from 'embercom/utils/knowledge-hub/sync-source-helpers';
import { type TableColumn } from 'embercom/lib/knowledge-hub/constants';
import type KnowledgeHubApi from 'embercom/lib/knowledge-hub/list-api';
import { type Predicate } from 'embercom/lib/knowledge-hub/constants';
import { type EntityType } from 'embercom/models/data/entity-types';
import type Folder from 'embercom/models/content-service/folder';
import {
  type SyncIssuesFilterOptions,
  type FolderFilterOption,
} from 'embercom/lib/content-service/search-api';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import { type Router } from '@ember/routing';
import { tracked } from 'tracked-built-ins';
import type KnowledgeHubContentWrapper from 'embercom/models/content-service/knowledge-hub-content-wrapper';
import { SourceType, SourceTypeName } from 'embercom/models/import-service/import-source';
import { ResyncType } from 'embercom/models/content-service/content-import-source';
import type ContentFragment from 'embercom/models/content-service/content-fragment';
import { isEmpty } from '@ember/utils';
import { taskFor } from 'ember-concurrency-ts';
import { restartableTask, dropTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { trackedInLocalStorage } from 'ember-tracked-local-storage';
import { SyncStatus } from 'embercom/models/knowledge-hub/sync-source-wrapper';
import { cached } from 'tracked-toolbox';
import { timeout } from 'ember-concurrency';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import {
  BULK_ACTION_PROGRESSION_ID,
  type BulkActionProgressionEvent,
  BulkActionProgressionValues,
  IMPORT_JOB_STATUS_EVENT_ID,
  IMPORT_JOB_DELETE_STATUS_ID,
  type UpdateImportSourceStatusEvent,
  type ImportServiceImportSourceDeletedEvent,
} from 'embercom/services/knowledge-hub-service';
import { RUN_STATUSES } from 'embercom/lib/external-content/constants';
import {
  CONTENT_IMPORT_RUN_STATUS_TOPIC,
  EVENT_SERVICE_ID,
  FIN_INGESTION_POLLING_INTERVAL,
  SYNC_SUMMARY_POLL_INTERVAL,
} from 'embercom/services/content-import-service';
import { SyncBehaviorType } from 'embercom/models/import-service/import-source';
import { task } from 'ember-concurrency-decorators';
import moment from 'moment-timezone';
import type ContentImportLogsModalService from 'embercom/services/content-import-logs-modal-service';
import ENV from 'embercom/config/environment';
import type ContentImportSource from 'embercom/models/content-service/content-import-source';
import { saveAdvancedSettings } from 'embercom/utils/knowledge-hub/advanced-settings-helpers';

export const AIV3_CONTENT_INGESTION_JOB_STATUS_CHANGED_EVENT_ID =
  'AiV3ContentIngestionJobStatusChanged';

const TRIGGER_SOURCE_ADMIN_INITIATED_SELECTIVE_RESYNC = 'admin_initiated_selective_resync';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    title: string;
    folder?: Folder;
    setSearchTerm: (searchTerm?: string) => void;
    searchTerm?: string;
    setLinkedUrls: (linkedUrls?: string[]) => void;
    linkedUrls?: string[];
    titleTerm?: string;
    setTitleTerm: (titleTerm?: string) => void;
    bodyTerm?: string;
    setBodyTerm: (bodyTerm?: string) => void;
    urlTerm?: string;
    setUrlTerm: (urlTerm?: string) => void;
    listApi: KnowledgeHubApi;
    locale: string | null;
    setLocale: (locale: string | null) => void;
    predicates?: Array<Predicate>;
    setPredicates: (predicates: Array<Predicate>) => void;
    status: string | null;
    setStatus: (status: string | null) => void;
    syncStatus?: Array<string> | null;
    setSyncStatus?: (syncStatus: Array<string> | null) => void;
    chatbotState?: string;
    setChatbotState: (chatbotState?: string) => void;
    copilotState?: string;
    setCopilotState: (copilotState?: string) => void;
    salesAgentState?: string;
    setSalesAgentState: (salesAgentState?: string) => void;
    createdBy?: string;
    setCreatedBy: (createdBy?: string) => void;
    selectedTypes?: Array<EntityType>;
    setSelectedTypes: (selectedTypes?: Array<EntityType>) => void;
    urlSourceId?: string;
    setUrlSourceId: (urlSourceId?: string) => void;
    selectedHelpCenterIds?: Array<string>;
    setSelectedHelpCenterIds: (selectedHelpCenterIds?: Array<string>) => void;
    lastUpdatedBy?: string;
    setLastUpdatedBy: (lastUpdatedBy?: string) => void;
    sortDirection?: SortDirection;
    sortBy?: string;
    setSortBy: (sortBy?: string) => void;
    setSortDirection: (sortDirection?: string) => void;
    displayFolderFilter?: boolean;
    selectedFolderFilterOption?: FolderFilterOption;
    setFolderFilterOption?: (folderFilterOption?: FolderFilterOption) => void;
    selectedSyncIssueOption?: SyncIssuesFilterOptions;
    setSyncIssueFilterOption?: (syncIssueOption?: SyncIssuesFilterOptions) => void;
    selectableColumns?: ColumnData[];
    resetFilters: () => void;
    activeFilter?: string;
    setActiveFilter: (filter?: string) => void;
    filtersApplied?: string[];
    setFiltersApplied: (filters: string[]) => void;
    tagIds?: string[];
    setTagIds: (tagIds?: string[]) => void;
    tagOperator?: TagOperator;
    setTagOperator: (tagOperator?: TagOperator) => void;
    collectionIds?: string[];
    setCollectionIds: (collectionIds?: string[]) => void;
    writtenByIds?: string[];
    setWrittenByIds: (writtenByIds?: string[]) => void;
    aiContentSegmentIds?: string[];
    setAiContentSegmentIds: (aiContentSegmentIds?: string[]) => void;
    parentFolderRoute?: string;
    allContentRoute?: string;
    // Indicates if this is the native content page (Articles) - used for breadcrumb display
    isNativeContentPage?: boolean;
    onNewContent?: () => void;
  };
}

export const SHOW_ALL_TRANSLATIONS = 'knowledge_hub_all_content_show_all_translations';

export enum SyncSteps {
  Connect = 'connect',
  ConfluenceSync = 'confluence-sync',
  GuruSync = 'guru-sync',
  NotionSync = 'notion-sync',
  SalesforceSync = 'salesforce-sync',
  BoxSync = 'box-sync',
  Document360Sync = 'document360-sync',
  ShopifySync = 'shopify-sync',
}

export const KNOWLEDGE_HUB_ALL_CONTENT_COLUMNS = 'knowledge_hub_all_content_columns';
export const KNOWLEDGE_HUB_ALL_CONTENT_COLUMNS_LAST_UPDATED_AT =
  'knowledge_hub_all_content_columns_last_updated_at';
export const KNOWLEDGE_HUB_WEB_SOURCE_COLUMNS = 'knowledge_hub_web_source_columns';
export const KNOWLEDGE_HUB_WEB_SOURCE_COLUMNS_LAST_UPDATED_AT =
  'knowledge_hub_web_source_columns_last_updated_at';
export const CONTENT_PERFORMANCE_ROUTE = 'apps.app.reports.customer-support.fin-ai-agent';
export const AI_AGENT_SETUP_ROUTE = 'apps.app.automation.fin-ai-agent.setup';
export const SPACER_COLUMN: TableColumn = { valuePath: 'spacer' };

// Unix timestamp for 15th November 2024 12:00:00 UTC
// A time before this PR was merged, so that we can test the new columns and not break navbar beta users over the weekend
export const HELP_CENTER_COLUMN_DEFAULT_CUTOFF_DATE = 1731672000;
// Threshold for determining when to reload entire list vs just update count display
const SMALL_LIST_RELOAD_THRESHOLD = 25;

export default class FilterableList extends Component<Signature> {
  @service declare appService: AppService;
  @service declare permissionsService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare router: Router;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare realTimeEventService: { on: Function; off: Function; subscribeTopics: Function };
  @service declare notificationsService: $TSFixMe;
  @service declare modalService: $TSFixMe;
  @service declare contentImportLogsModalService: ContentImportLogsModalService;

  @tracked showCreateFolderModal = false;
  @tracked columnsWhenFiltering = this.columns;
  @tracked selectedFragments: Set<ContentFragment> = new Set();
  @tracked unselectedFragments: Set<ContentFragment> = new Set();
  @tracked isBulkSelection = false;
  @tracked showAllToggled = false;
  @tracked importedItemsCount: number | null = null;
  @tracked previousImportedItemsCount: number | null = null;
  @tracked hasSyncProgressData = false;
  @tracked showNewContentModal = false;
  @tracked showPreview = false;
  @tracked syncProgressText = { imported: 0, available: 0 };
  @tracked aiV3ContentIngestionReloadTimer: any = null;

  // Preview state management
  @tracked previewLocale = 'en';
  @tracked previewMode: 'agent' | 'copilot' = 'agent';

  @trackedInLocalStorage({
    keyName: KNOWLEDGE_HUB_ALL_CONTENT_COLUMNS,
  })
  columnsLocalStorage: string | undefined;

  @trackedInLocalStorage({
    keyName: KNOWLEDGE_HUB_ALL_CONTENT_COLUMNS_LAST_UPDATED_AT,
  })
  columnsLastUpdatedAtLocalStorage: number | undefined;

  @trackedInLocalStorage({
    keyName: KNOWLEDGE_HUB_WEB_SOURCE_COLUMNS,
  })
  webSourceColumnsLocalStorage: string | undefined;

  @trackedInLocalStorage({
    keyName: KNOWLEDGE_HUB_WEB_SOURCE_COLUMNS_LAST_UPDATED_AT,
  })
  webSourceColumnsLastUpdatedAtLocalStorage: number | undefined;

  constructor(owner: unknown, args: any) {
    super(owner, args);

    let showAllTranslations = localStorage.getItem(SHOW_ALL_TRANSLATIONS);
    this.showAllToggled = showAllTranslations === 'true';

    // Initialize preview locale based on app settings
    this.previewLocale = this.appService.app.locale || 'en';
    if (this.appService.app.canUseFinPreviewAutoLanguageDetection) {
      this.previewLocale = 'auto';
    }

    this.knowledgeHubService.subscribeToImportServiceUpdates();
    this.setImportServiceUpdateActions();
    this.setBulkActionProgressActions();
    this.subscribeToImportEvents();
    this.subscribeToAiV3ContentIngestionEvents();

    // Initialize sync progress if needed
    if (this.args.folder?.firstTimeSyncing && this.isWebSourceFolder) {
      this.startPolling();
    }
  }

  async willDestroy(): Promise<void> {
    super.willDestroy();
    this.destroyNexusEvents();

    if (this.aiV3ContentIngestionReloadTimer) {
      clearTimeout(this.aiV3ContentIngestionReloadTimer);
      this.aiV3ContentIngestionReloadTimer = null;
    }

    this.unsubscribeFromAiV3ContentIngestionEvents();
    this.unsubscribeFromImportEvents();
    if (this.args.folder?.firstTimeSyncing || this.syncSource?.isSyncing) {
      this.stopPolling();
    }
  }

  get defaultColumns(): TableColumn[] {
    // Use the former web source defaults as the new universal defaults
    // This includes sync-status, AI Agent, and audience columns for all content types
    return [
      ...KNOWLEDGE_HUB_REQUIRED_COLUMNS,
      ALL_CONTENT_COLUMN_DATA.syncStatus,
      ALL_CONTENT_COLUMN_DATA.chatbotState,
      ALL_CONTENT_COLUMN_DATA.salesAgentState,
      ALL_CONTENT_COLUMN_DATA.segment,
    ];
  }

  get columns(): TableColumn[] {
    let columns: TableColumn[];

    // Use appropriate local storage based on folder type
    let storedColumns = this.isWebSourceFolder
      ? this.webSourceColumnsLocalStorage
      : this.columnsLocalStorage;

    if (storedColumns) {
      columns = JSON.parse(storedColumns) as TableColumn[];
    } else if (this.args.folder) {
      let hasMultilingual = this.args.listApi.contentWrappers.any(
        (contentWrapper: KnowledgeHubContentWrapper) => contentWrapper?.isMultilingual,
      );
      columns = hasMultilingual ? FOLDER_MULTILINGUAL_DEFAULT_COLUMNS : this.defaultColumns;
    } else {
      columns = this.defaultColumns;
    }

    // Overwrite the columns if the user has last updated their columns before the cutoff date
    // See PR 90846 for more details
    let lastUpdatedAt = this.isWebSourceFolder
      ? this.webSourceColumnsLastUpdatedAtLocalStorage
      : this.columnsLastUpdatedAtLocalStorage;

    if (lastUpdatedAt === undefined || lastUpdatedAt < HELP_CENTER_COLUMN_DEFAULT_CUTOFF_DATE) {
      // Only apply Help Center column additions to non-web source folders
      if (!this.isWebSourceFolder) {
        let newColumns = [
          ALL_CONTENT_COLUMN_DATA.collectionNames,
          ALL_CONTENT_COLUMN_DATA.articlePublishedState,
          ALL_CONTENT_COLUMN_DATA.updatedAt,
          ALL_CONTENT_COLUMN_DATA.status,
        ];

        // Filter out duplicates based on valuePath
        columns = [
          ...columns,
          ...newColumns.filter(
            (newCol) => !columns.some((existingCol) => existingCol.valuePath === newCol.valuePath),
          ),
        ];

        // Sort based on KNOWLEDGE_HUB_EVERY_CONTENT_COLUMN order
        let columnOrder = KNOWLEDGE_HUB_EVERY_CONTENT_COLUMN_ORDER;
        columns.sort((a, b) => columnOrder.indexOf(a.valuePath) - columnOrder.indexOf(b.valuePath));
      }
    }

    columns = this.filterAndUpdateColumnTranslations(columns);

    // We need to ensure that the HC sign post component is rendered to customers who have visited this page and have columns stored in local storage
    // Otherwise, we'd need them to remove & re-add the column to see the HC sign post component
    let statusColumn = columns.find((column) => column.valuePath === 'status');
    if (statusColumn && !statusColumn.customHeaderComponent) {
      statusColumn.customHeaderComponent =
        'knowledge-hub/filterable-list/table/custom-column-headers/help-center-status';
    }

    // Filtering out the helpCenterName column if present in the local storage of users having saved this in their filter list
    let deprecatedColumnNames = ['helpCenterName'];
    columns = columns.filter((column) => !deprecatedColumnNames.includes(column.valuePath));

    // Add a spacer column to the end of the columns array
    columns.push(SPACER_COLUMN);

    return columns;
  }

  set columns(columns: TableColumn[]) {
    let timestamp = Math.floor(Date.now() / 1000); // Convert to seconds

    if (this.isWebSourceFolder) {
      this.webSourceColumnsLocalStorage = JSON.stringify(columns);
      this.webSourceColumnsLastUpdatedAtLocalStorage = timestamp;
    } else {
      this.columnsLocalStorage = JSON.stringify(columns);
      this.columnsLastUpdatedAtLocalStorage = timestamp;
    }
  }

  get columnsToDisplay() {
    if (this.hasAppliedFilter) {
      return this.columnsWhenFiltering;
    }
    return this.columns;
  }

  get selectableColumns() {
    let columns = this.args.selectableColumns ?? DEFAULT_SELECTABLE_COLUMNS;
    return this.filterAndUpdateColumnTranslations(columns);
  }

  get syncSource() {
    let rootFolder = findRootSyncedFolder(this.args.folder);
    if (!rootFolder) {
      return undefined;
    }
    return getSyncSourceForFolder(this.store, rootFolder.id);
  }

  get contentImportSourceSyncCounts() {
    if (!this.args.folder?.contentImportSourceId) {
      return undefined;
    }
    let contentImportSource = this.store.peekRecord(
      'content-service/content-import-source',
      this.args.folder.contentImportSourceId,
    );

    return contentImportSource?.statusCounts;
  }

  @cached
  get totalSyncedCount() {
    return (
      this.args.listApi.displayFolderCounts?.[this.folderIdentifier]?.status_counts?.total ?? 0
    );
  }

  @cached
  get failedSyncsCount() {
    return (
      this.args.listApi.displayFolderCounts?.[this.folderIdentifier]?.status_counts?.failed_count ??
      0
    );
  }

  @cached
  get successfulSyncsCount() {
    return (
      this.args.listApi.displayFolderCounts?.[this.folderIdentifier]?.status_counts?.passed_count ??
      0
    );
  }

  @cached
  get excludedSyncsCount() {
    return (
      this.args.listApi.displayFolderCounts?.[this.folderIdentifier]?.status_counts
        ?.excluded_count ?? 0
    );
  }

  @cached
  get syncingCount() {
    return (
      this.args.listApi.displayFolderCounts?.[this.folderIdentifier]?.status_counts
        ?.syncing_count ?? 0
    );
  }
  @cached
  get folderIdentifier() {
    return `${this.args.folder?.entityType}_${this.args.folder?.id}`;
  }

  @cached
  get hasLoadedAllContent() {
    return this.args.listApi.contentWrappers.length >= this.args.listApi.totalCount;
  }

  @cached
  get syncStatus(): any {
    if (this.syncSource?.isSyncing) {
      let syncingText =
        this.args.folder?.firstTimeSyncing && this.syncProgressText.available > 0
          ? this.intl.t('knowledge-hub.sync-progress-count', {
              imported: this.syncProgressText.imported,
              available: this.syncProgressText.available,
            })
          : this.intl.t('knowledge-hub.sync-progress');

      let tooltipText: string | null = null;

      if (this.args.folder?.firstTimeSyncing && this.syncProgressText.available > 0) {
        tooltipText = this.intl.t('knowledge-hub.sync-progress-count-tooltip');
      } else if (this.isResyncingFailedPages || this.syncSource?.isSyncing) {
        if (this.syncingCount > 0) {
          tooltipText = this.intl.t('knowledge-hub.sync-progress-selective-resync-tooltip', {
            count: this.syncingCount,
          });
        }
      }

      return {
        stampText: syncingText,
        color: 'notice-fill',
        statusDotClasses: 'border-4 bg-notice-fill-emphasis border-notice-container',
        tooltip: tooltipText,
      };
    }

    let hasMajorityFailures = () => {
      return this.failedSyncsCount > this.totalSyncedCount / 2;
    };

    let stampColor = 'text-success';
    if (this.syncSource?.isSyncing) {
      stampColor = 'text-notice';
    } else if (hasMajorityFailures()) {
      stampColor = 'text-error';
    }
    return {
      stampText: hasMajorityFailures()
        ? this.intl.t('knowledge-hub.sync-total-count-failed', {
            failedSyncs: this.failedSyncsCount,
            totalSynced: this.totalSyncedCount,
          })
        : this.intl.t('knowledge-hub.sync-total-count-live', {
            liveSyncs: this.successfulSyncsCount,
            totalSynced: this.totalSyncedCount,
          }),
      syncingText:
        this.args.folder?.firstTimeSyncing && this.syncProgressText.available > 0
          ? this.intl.t('knowledge-hub.sync-progress-count', {
              imported: this.syncProgressText.imported,
              available: this.syncProgressText.available,
            })
          : this.intl.t('knowledge-hub.sync-progress'),
      failedSyncsText:
        this.failedSyncsCount > 0
          ? this.intl.t('knowledge-hub.sync-failed-pages-count', {
              failedSyncs: this.failedSyncsCount,
            })
          : '',
      successfulSyncsText:
        this.successfulSyncsCount > 0
          ? this.intl.t('knowledge-hub.sync-success-pages-count', {
              successfulSyncs: this.successfulSyncsCount,
            })
          : '',
      excludedSyncsText:
        this.excludedSyncsCount > 0
          ? this.intl.t('knowledge-hub.sync-excluded-pages-count', {
              excludedSyncs: this.excludedSyncsCount,
            })
          : '',
      color: stampColor,
      statusDotClasses: hasMajorityFailures()
        ? 'border-4 bg-error-fill-emphasis border-error-container'
        : 'border-4 bg-success-fill-emphasis border-success-container',
    };
  }

  get showManageSourcesButton() {
    let rootFolder = findRootSyncedFolder(this.args.folder);
    return rootFolder && getSyncSourceForFolder(this.store, rootFolder.id);
  }

  get isWebSourceFolder(): boolean {
    return this.args.folder?.sourceTypeName === SourceTypeName[SourceType.Web];
  }

  get showStatusFilter(): boolean {
    return this.isWebSourceFolder;
  }

  get isResyncingFailedPages(): boolean {
    return taskFor(this.resyncFailedPages).isRunning;
  }

  get showEmptyState() {
    return (
      !this.args.listApi.isLoadingPage &&
      this.args.listApi.totalCount === 0 &&
      !this.hasAppliedFilter
    );
  }

  get treeItem() {
    if (this.args.folder) {
      return this.args.folder.treeItem;
    }
    // When no folder is specified, use the NavigationLinkData item (Articles link)
    // Fallback to first child if navigation link item not found
    return (
      this.knowledgeHubService.nativeContentTreeItem ||
      this.knowledgeHubService.tree?.children.firstObject
    );
  }

  get showAllTranslations() {
    return this.showAllToggled || this.args.listApi.requiresShowTranslations;
  }

  get hasAppliedFilter(): boolean {
    return (
      !isEmpty(this.args.searchTerm) ||
      !isEmpty(this.args.selectedTypes) ||
      !isEmpty(this.args.filtersApplied) ||
      !isEmpty(this.args.linkedUrls) ||
      !isEmpty(this.args.titleTerm) ||
      !isEmpty(this.args.bodyTerm) ||
      !isEmpty(this.args.urlTerm) ||
      !isEmpty(this.args.syncStatus)
    );
  }

  get isNewSubFolderDisabled() {
    return this.args.folder?.atMaxDepth();
  }

  get isWebContentFirstTimeSyncing(): boolean {
    return !!(
      this.args.folder &&
      this.args.folder.sourceTypeName === SourceTypeName[SourceType.Web] &&
      this.args.folder.firstTimeSyncing
    );
  }

  get allContentRoute(): string {
    return this.args.allContentRoute ?? 'apps.app.knowledge-hub';
  }

  get lastSyncText(): string | null {
    if (!this.syncSource?.lastSyncedAt) {
      return '';
    }
    let lastSyncDate = moment(this.syncSource.lastSyncedAt).fromNow();
    return this.intl.t('knowledge-hub.last-synced', { date: lastSyncDate });
  }

  get shouldShowTooltipContent(): boolean {
    if (this.syncSource?.isSyncing) {
      return !!(
        (this.args.folder?.firstTimeSyncing && this.hasSyncProgressData) ||
        this.syncingCount > 0
      );
    }
    return true;
  }

  get showImportSourcesTiles(): boolean {
    if (this.args.folder) {
      return false;
    }
    return this.args.isNativeContentPage !== true;
  }

  get showAddContentTiles(): boolean {
    if (this.args.folder) {
      return true;
    }
    return this.args.isNativeContentPage === true;
  }

  @action
  togglePreview() {
    this.showPreview = !this.showPreview;
  }

  @action
  navigateToOverview() {
    if (this.appService.app.canUseStandalone) {
      this.router.transitionTo('apps.app.knowledge-hub.overview');
    }
  }

  @action
  async openCreateFolderModal() {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (e) {
      return;
    }
    this.trackAnalyticsEvent(
      'clicked',
      !this.args.folder ? 'new_folder_button' : 'new_subfolder_button',
    );

    this.showCreateFolderModal = true;
  }

  @action beforeSearch() {
    if (!this.args.listApi.isFiltering) {
      // This is the first filter / search being applied
      this.ensureFolderColumnVisible();
    }
  }

  @action ensureFolderColumnVisible() {
    let otherColumns = this.columnsWhenFiltering.reject(
      (column) =>
        column.valuePath === ALL_CONTENT_COLUMN_DATA.folder.valuePath ||
        column.valuePath === SPACER_COLUMN.valuePath,
    );
    this.columnsWhenFiltering = otherColumns.concat(ALL_CONTENT_COLUMN_DATA.folder, SPACER_COLUMN);
  }

  @action
  setSelectedFragments(fragments: Set<ContentFragment>) {
    this.selectedFragments = fragments;
  }

  @action
  setUnselectedFragments(fragments: Set<ContentFragment>) {
    this.unselectedFragments = fragments;
    if (this.isBulkSelection && this.unselectedFragments.size > 0) {
      this.setIsBulkSelection(false);
    }
  }

  @action
  setIsBulkSelection(state: boolean) {
    this.isBulkSelection = state;
  }

  @action
  updatedSelectedTableColumns(columns: string[]) {
    let selectedColumns = columns.map((col) => ALL_CONTENT_COLUMN_DATA[col]);
    this.columns = KNOWLEDGE_HUB_REQUIRED_COLUMNS.concat(selectedColumns);
    this.columnsWhenFiltering = this.columns;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'update_columns',
      object: 'all_content',
      place: 'knowledge_hub',
    });
  }

  @action
  resetSelectedFragments() {
    this.selectedFragments = new Set();
    this.unselectedFragments = new Set();
    this.isBulkSelection = false;
  }

  @action
  toggleTranslations() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'toggle_translations_button',
      section: 'knowledge_hub',
      context: 'all_content',
      place: 'all_content',
    });
    this.showAllToggled = !this.showAllToggled;
    this.args.listApi.requiresShowTranslations = false;
    localStorage.setItem(SHOW_ALL_TRANSLATIONS, this.showAllToggled ? 'true' : 'false');
  }

  @action resetFilters() {
    this.args.resetFilters();
    this.columnsWhenFiltering = this.columns;
    this.args.listApi.reset();
    taskFor(this.reloadPage).perform();
  }

  @restartableTask *reloadPage(): TaskGenerator<void> {
    yield this.args.listApi.loadPage();
  }

  @dropTask
  *resyncFailedPages(): TaskGenerator<void> {
    this.trackAnalyticsEvent('clicked', 'resync_failed_pages_button');

    if (!this.args.folder?.contentImportSourceId) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.filterable-list.sources.actions.resync-error'),
      );
      return;
    }

    let contentImportSource = this.store.peekRecord(
      'content-service/content-import-source',
      this.args.folder.contentImportSourceId,
    );

    if (!contentImportSource) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.filterable-list.sources.actions.resync-error'),
      );
      return;
    }

    if (this.syncSource) {
      this.syncSource.status = SyncStatus.SYNCING;
    }

    yield contentImportSource.triggerResync(undefined, ResyncType.CONTENT_STATE, States.ERROR);

    yield taskFor(this.reloadList).perform();
  }

  @action toggleNewContentModal(state = false): void {
    this.showNewContentModal = state;
    this.trackAnalyticsEvent('clicked', 'new_content_button');
  }

  @action
  setPreviewMode(mode: 'agent' | 'copilot') {
    this.previewMode = mode;
  }

  @action
  setPreviewLocale(locale: string) {
    this.previewLocale = locale;
  }

  @action
  openReactContentImportLogsSheet() {
    this.contentImportLogsModalService.openContentImportLogsSheet(this.syncSource!);
  }

  private subscribeToImportEvents() {
    this.realTimeEventService.subscribeTopics([CONTENT_IMPORT_RUN_STATUS_TOPIC]);
    this.realTimeEventService.on(EVENT_SERVICE_ID, this, 'handleImportStatusChange');
  }

  private subscribeToAiV3ContentIngestionEvents() {
    this.realTimeEventService.on(
      AIV3_CONTENT_INGESTION_JOB_STATUS_CHANGED_EVENT_ID,
      this,
      'handleAiV3ContentIngestionJobStatusChange',
    );
  }

  private unsubscribeFromImportEvents() {
    this.realTimeEventService.off(EVENT_SERVICE_ID, this, 'handleImportStatusChange');
  }

  private unsubscribeFromAiV3ContentIngestionEvents() {
    this.realTimeEventService.off(
      AIV3_CONTENT_INGESTION_JOB_STATUS_CHANGED_EVENT_ID,
      this,
      'handleAiV3ContentIngestionJobStatusChange',
    );
  }

  // @ts-ignore - Used as event handler callback in subscribeToImportEvents
  private async handleImportStatusChange(event: any) {
    if (event.event_type === RUN_STATUSES.completed || event.event_type === RUN_STATUSES.failed) {
      if (this.syncSource && this.syncSource.sourceId === event.content_import_source_id) {
        this.syncSource.status =
          event.event_type === RUN_STATUSES.completed ? SyncStatus.SYNCED : SyncStatus.ERROR;
      }

      // Reload the content-import-source model to get updated statusCounts
      if (this.args.folder?.contentImportSourceId) {
        await this.store.findRecord(
          'content-service/content-import-source',
          this.args.folder.contentImportSourceId,
          { reload: true },
        );
      }

      // Check if this is a selective resync
      let isSelectiveResync =
        event.trigger_source === TRIGGER_SOURCE_ADMIN_INITIATED_SELECTIVE_RESYNC;

      if (this.args.folder?.firstTimeSyncing) {
        await taskFor(this.reloadList).perform();
        this.stopPolling();
        // perform delayed reload for ai v3 ingestion
        this.performDelayedReload();
      } else if (
        isSelectiveResync ||
        this.syncSource?.sourceId === event.content_import_source_id
      ) {
        // Reload for selective resyncs or when the event matches the current folder's source
        await taskFor(this.reloadList).perform();
      }
    }
  }

  // @ts-ignore - Used as event handler callback in subscribeToAiV3ContentIngestionEvents
  private handleAiV3ContentIngestionJobStatusChange(event: any) {
    if (this.aiV3ContentIngestionReloadTimer) {
      clearTimeout(this.aiV3ContentIngestionReloadTimer);
      this.performDelayedReload();
    }
  }

  private performDelayedReload() {
    this.aiV3ContentIngestionReloadTimer = setTimeout(() => {
      taskFor(this.reloadList).perform();
      this.aiV3ContentIngestionReloadTimer = null;
    }, FIN_INGESTION_POLLING_INTERVAL);
  }

  private startPolling() {
    if (!taskFor(this.pollSyncProgressAndRefreshList).isRunning) {
      taskFor(this.pollSyncProgressAndRefreshList).perform();
    }
  }

  private stopPolling() {
    taskFor(this.pollSyncProgressAndRefreshList).cancelAll();
  }

  @task({ restartable: true, maxConcurrency: 1 })
  private *pollSyncProgressAndRefreshList(): TaskGenerator<void> {
    while (true) {
      if (!this.shouldContinuePolling()) {
        break;
      }

      yield this.updateSyncProgressTextAndRefreshList();
      yield timeout(SYNC_SUMMARY_POLL_INTERVAL);
    }
  }

  private async updateSyncProgressTextAndRefreshList(): Promise<void> {
    try {
      if (!this.shouldContinuePolling()) {
        this.clearSyncProgressData();
        return;
      }

      let syncSummary = await this.fetchSyncSummary();
      if (!syncSummary) {
        this.clearSyncProgressData();
        return;
      }

      let { importedCount, availableCount } = this.extractCounts(syncSummary);
      this.syncProgressText = {
        imported: importedCount,
        available: availableCount,
      };
      try {
        await this.reloadListIfNeeded(importedCount);
        this.updateProgressState();
      } finally {
        // Always update previousImportedItemsCount to prevent infinite reload attempts
        this.previousImportedItemsCount = importedCount;
      }
    } catch (error) {
      this.setFallbackProgressState();
    }
  }

  private shouldContinuePolling(): boolean {
    return !!(
      this.args.folder?.contentImportSourceId &&
      (this.args.folder?.firstTimeSyncing || this.syncSource?.isSyncing)
    );
  }

  private clearSyncProgressData(): void {
    this.syncProgressText = { imported: 0, available: 0 };
    this.hasSyncProgressData = false;
  }

  private async fetchSyncSummary() {
    if (!this.args.folder?.contentImportSourceId) {
      return null;
    }

    return await this.knowledgeHubService.getSyncSummary(
      String(this.args.folder.contentImportSourceId),
    );
  }

  private extractCounts(response: any): { importedCount: number; availableCount: number } {
    let importedCount = response?.[0]?.imported ?? 0;
    let availableCount = response?.[0]?.available ?? 0;
    return { importedCount, availableCount };
  }

  private setFallbackProgressState(): void {
    this.syncProgressText = { imported: 0, available: 0 };
    this.hasSyncProgressData = false;
  }

  @restartableTask *reloadList(): TaskGenerator<void> {
    yield this.args.listApi.refreshPageAfterDelay(ENV.APP._1000MS);
  }

  setImportServiceUpdateActions() {
    this.realTimeEventService.on(
      IMPORT_JOB_STATUS_EVENT_ID,
      this,
      'updateImportSourceStatusAndNotify',
    );
    this.realTimeEventService.on(
      IMPORT_JOB_DELETE_STATUS_ID,
      this,
      'resetFiltersAndNotifyDeletion',
    );
  }

  setBulkActionProgressActions() {
    this.realTimeEventService.on(BULK_ACTION_PROGRESSION_ID, this, 'handleBulkActionProgression');
  }

  private destroyNexusEvents() {
    this.realTimeEventService.off(BULK_ACTION_PROGRESSION_ID, this, 'handleBulkActionProgression');
  }

  async updateImportSourceStatusAndNotify(event: UpdateImportSourceStatusEvent) {
    let source = this.store.peekRecord('import-service/import-source', event.import_source_id);
    if (!source) {
      return;
    }

    source.status = event.status;

    if (event.status === IMPORT_SOURCE_STATUSES.success) {
      if (event.sync_behavior === SyncBehaviorType.Sync) {
        this.knowledgeHubService.notifySourceStateChangeConfirmation(
          this.intl.t('knowledge-hub.filterable-list.sources.nexus-event-sync-content-success'),
          `${event.import_source_id}`,
        );
      }

      if (event.sync_behavior === SyncBehaviorType.Import) {
        this.knowledgeHubService.notifySourceStateChangeConfirmation(
          this.intl.t('knowledge-hub.filterable-list.sources.import-complete', {
            sourceName: source.sourceName,
          }),
          `${event.import_source_id}`,
        );
      }

      this.resetFilters();
      this.hardReloadImportSources();
    }
  }

  async resetFiltersAndNotifyDeletion(event: ImportServiceImportSourceDeletedEvent) {
    if (event.is_deleted) {
      this.knowledgeHubService.notifySourceStateChangeConfirmation(
        this.intl.t('knowledge-hub.filterable-list.sources.nexus-event-delete-success'),
        `${event.import_source_id}`,
      );
      this.resetFilters();
      this.store.unloadAll('content-service/folder'); // otherwise deleted synced folders stick around
      let folderIds = (await this.knowledgeHubService.fetchFoldersOnce(true)).map(
        (folder) => folder.id,
      );

      if (this.args.folder && !folderIds.includes(this.args.folder?.id)) {
        // if the active folder isn't in the new list of folders it was deleted
        // so navigate to root folder
        this.router.transitionTo('apps.app.knowledge-hub.all-content');
      }
    } else {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.filterable-list.sources.nexus-event-delete-error'),
      );
    }
  }

  async handleBulkActionProgression(event: BulkActionProgressionEvent) {
    let currentAdmin = this.appService.app.currentAdmin;
    if (currentAdmin.id !== event.admin_id.toString()) {
      return;
    }

    if (event.status === BulkActionProgressionValues.Complete) {
      if (event.all_entities_unaffected) {
        this.knowledgeHubService.notifyNoEntitiesAffected(
          event.bulk_action,
          this.appService.app.canUseStandalone,
        );
      } else {
        taskFor(this.reloadList).perform();
        this.sendCompletionNotification(event);
      }
    }

    this.knowledgeHubService.handleBulkActionFailure(event);
  }

  private async reloadListIfNeeded(importedCount: number): Promise<void> {
    if (this.previousImportedItemsCount === null) {
      if (importedCount > 0) {
        // Initial items imported, reload to show them
        await taskFor(this.reloadList).perform();
      }
      return;
    }

    let hasTotalCountChanged = this.previousImportedItemsCount !== importedCount;

    if (!hasTotalCountChanged) {
      return;
    }

    if (this.args.listApi.totalCount <= SMALL_LIST_RELOAD_THRESHOLD) {
      await taskFor(this.reloadList).perform();
    } else {
      // For large lists, just update the count display instead of reloading
      this.importedItemsCount = importedCount;
    }
  }

  private updateProgressState(): void {
    this.previousImportedItemsCount = this.importedItemsCount;
    this.hasSyncProgressData = this.syncProgressText.available > 0;
  }

  private async sendCompletionNotification(event: BulkActionProgressionEvent) {
    let action = event.bulk_action;
    let unaffectedEntityTypes = event.unaffected_entity_types;
    let language_code = event.language_code;
    let destinationFolderId = event.destination_folder_id;
    this.knowledgeHubService.notifyBulkActionCompleted(
      action,
      unaffectedEntityTypes,
      language_code,
      destinationFolderId,
      this.appService.app.canUseStandalone,
    );
    if (destinationFolderId) {
      await this.knowledgeHubService.fetchFoldersOnce(true);
    }
  }

  hardReloadImportSources() {
    this.store.findAll('import-service/import-source', { reload: true });
  }

  private trackAnalyticsEvent(action: string, object: string, metadata?: any): void {
    this.intercomEventService.trackAnalyticsEvent({
      action,
      object,
      section: 'knowledge_hub',
      context: 'all_content',
      place: 'all_content',
      ...metadata,
    });
  }

  private filterAndUpdateColumnTranslations<T extends TableColumn | ColumnData>(columns: T[]): T[] {
    return this.updateColumnTranslations(this.filterColumns(columns));
  }

  private updateColumnTranslations<T extends TableColumn | ColumnData>(columns: T[]): T[] {
    return columns.map((column) => {
      let updatedColumn = { ...column };
      let valuePath = 'valuePath' in column ? column.valuePath : column.value;

      // Rename "AI Agent" to "Fin Service Agent" when Sales Agent column is visible
      if (this.appService.app.canUseFinSdrTeammateUi && valuePath === 'chatbotState') {
        updatedColumn.labelIntlKey =
          'knowledge-hub.filterable-list.list.columns.chatbot-state-with-sales-agent';
      }

      if (this.appService.app.canUseStandalone) {
        if (valuePath === 'copilotState') {
          // Use Fin Copilot instead of Copilot for standalone apps
          updatedColumn.labelIntlKey =
            'knowledge-hub.filterable-list.list.columns.copilot-state-standalone';
        }

        if (valuePath === 'chatbotState') {
          if (this.appService.app.canUseCopilotOnStandalone) {
            // Use Fin Agent instead of AI Agent for standalone apps
            updatedColumn.labelIntlKey =
              'knowledge-hub.filterable-list.list.columns.chatbot-state-standalone';
          } else {
            // With copilot disabled, we want to just show Enabled the full AI Agent name instead of the simplified name
            updatedColumn.labelIntlKey =
              'knowledge-hub.filterable-list.list.columns.chatbot-state-standalone-without-copilot';
          }
        }

        if (valuePath === 'segment') {
          // Use Audience instead of Fin audience for standalone apps
          updatedColumn.labelIntlKey =
            'knowledge-hub.filterable-list.list.columns.segment-standalone';
        }
      }

      return updatedColumn;
    });
  }

  // This function filters out of the TableColumns or ColumnData any columns that are not relevant to the current app
  // due to feature flags.
  private filterColumns<T extends TableColumn | ColumnData>(columns: T[]): T[] {
    let columnsToFilterOut: string[] = [];
    if (!this.appService.app.canUseExternalAi) {
      columnsToFilterOut = ['chatbotState', 'copilotState', 'salesAgentState'];
    }

    if (this.appService.app.canUseStandalone) {
      columnsToFilterOut = [
        'status',
        'articlePublishedState',
        'collectionNames',
        'folder',
        'syncStatus',
      ];

      if (!this.appService.app.canUseCopilotOnStandalone) {
        columnsToFilterOut.push('copilotState');
      }
    }

    // Web folder specific filtering
    if (this.isWebSourceFolder) {
      columnsToFilterOut.push('collectionNames', 'articlePublishedState', 'writtenBy');
    } else {
      // Hide sync-status column when NOT a web source folder
      columnsToFilterOut.push('syncStatus');
    }

    // Fin/Content shouldn't show HC or CoPilot related columns
    // Based on route, if null uses defualt of KH
    // status == HC state
    if (this.args.parentFolderRoute) {
      columnsToFilterOut.push(
        'status', // HC state
        'helpCenterState',
        'collectionNames',
      );
    }
    // Content status i.e. counts
    // Currently only applicable to Unified Content page
    columnsToFilterOut.push('contentStatus');
    columnsToFilterOut.push('helpCenterState');

    if (!this.appService.app.canUseFinSdrTeammateUi) {
      columnsToFilterOut.push('salesAgentState');
    }

    return columns.filter(
      (column) =>
        !columnsToFilterOut.includes('valuePath' in column ? column.valuePath : column.value),
    );
  }

  @action
  async openAdvancedSettingsSheet(contentImportSource: ContentImportSource) {
    this.modalService.openModal(
      'react/knowledge-hub/advanced-settings-websync/advanced-settings-websync-sheet',
      {
        appId: this.appService.app.id,
        adminId: this.appService.app.currentAdmin.id,
        contentImportSource,
        onSave: async (formData: Record<string, unknown>) => {
          await this.saveAdvancedSettings(contentImportSource, formData, false);
        },
        onSaveAndSync: async (formData: Record<string, unknown>) => {
          await this.saveAdvancedSettings(contentImportSource, formData, true);
        },
      },
    );
  }

  private async saveAdvancedSettings(
    contentImportSource: ContentImportSource,
    formData: Record<string, unknown>,
    shouldResync: boolean,
  ) {
    await saveAdvancedSettings({
      contentImportSource,
      formData,
      shouldResync,
      intl: this.intl,
      notificationsService: this.notificationsService,
      onRefresh: async () => {
        await this.args.listApi.refreshPageAfterDelay();
      },
    });
  }
}
declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentSetupV2::FilterableList': typeof FilterableList;
    'knowledge-hub/filterable-list/filterable-list': typeof FilterableList;
  }
}
